import { ActionTree } from 'vuex'

import api from '@/apiClient'

import { PaymentMethod, PaymentMethodPayload } from '@/models'

import { RootState } from '../state'

import { PaymentMethodState } from './state'
import {
  ADD_ALLOWED_PAYMENT_METHOD,
  REMOVE_ALLOWED_PAYMENT_METHOD,
  UPDATE_PAYMENT_METHODS,
  UPDATE_ALLOWED_PAYMENT_METHODS
} from './mutations'
import { mergeById } from '@/utils'

const actions: ActionTree<PaymentMethodState, RootState> = {
  addAllowedPaymentMethod ({ commit }, paymentMethod: PaymentMethod) {
    commit(ADD_ALLOWED_PAYMENT_METHOD, paymentMethod)
  },

  async loadPaymentMethodList ({ commit, state }) {
    const list = await api.paymentMethods.fetchPaymentMethods()

    if (list.length > 0) {
      const paymentMethods = mergeById(state.allPaymentMethods.elements, list)

      commit(UPDATE_PAYMENT_METHODS, paymentMethods)
    } else {
      commit(UPDATE_PAYMENT_METHODS, [])
    }
  },

  async loadAllowedPaymentMethodList ({ commit, rootGetters, rootState, state }) {
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    const list = await api.paymentMethods.fetchAllowedPaymentMethods(providerId, businessUnit.id)

    if (list.length > 0) {
      const paymentMethods = mergeById(state.allowedPaymentMethods.elements, list)

      commit(UPDATE_ALLOWED_PAYMENT_METHODS, paymentMethods)
    } else {
      commit(UPDATE_ALLOWED_PAYMENT_METHODS, [])
    }
  },

  async fetchAllowedPaymentMethod ({ commit, dispatch, getters, rootGetters, rootState, state }, id: number) {
    const token = rootState.auth.token
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null || token === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    try {
      const paymentMethod = await api.paymentMethods.fetchAllowedPaymentMethod(token, providerId, businessUnit.id, id)

      if (getters.allowedPaymentMethodById(id) === null) {
        await dispatch('addAllowedPaymentMethod', paymentMethod)
      } else {
        const paymentMethods = mergeById(state.allowedPaymentMethods.elements, [paymentMethod])
        commit(UPDATE_ALLOWED_PAYMENT_METHODS, paymentMethods)
      }
    } catch (error) {
      if (error.response) {
        const { status } = error.response
        if (status === 401) {
          dispatch('logout')
        }
      }
    }
  },

  async bindPaymentMethod ({ dispatch, rootGetters, rootState }, payload: PaymentMethodPayload) {
    const token = rootState.auth.token
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null || token === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    try {
      const paymentMethod = await api.paymentMethods.bindPaymentMethod(token, providerId, businessUnit.id, payload)
      await dispatch('addAllowedPaymentMethod', paymentMethod)
    } catch (error) {
      if (error.response) {
        const { status } = error.response
        if (status === 401) {
          dispatch('logout')
        }
      }
    }
  },

  async unbindPaymentMethod ({ commit, dispatch, rootGetters, rootState }, id: number) {
    const token = rootState.auth.token
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null || token === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    try {
      await api.paymentMethods.unbindPaymentMethod(token, providerId, businessUnit.id, id)

      commit(REMOVE_ALLOWED_PAYMENT_METHOD, id)
    } catch (error) {
      if (error.response) {
        const { status } = error.response
        if (status === 401) {
          dispatch('logout')
        }
      }
    }
  },

  async updateAllowedPaymentMethod ({ commit, dispatch, rootGetters, rootState, state }, payload: PaymentMethodPayload) {
    const token = rootState.auth.token
    const businessUnit = rootState.businessUnit
    const providerId: number | null = rootGetters['auth/currentProvider']

    if (providerId === null || token === null) {
      throw new Error('User is not authenticated')
    }

    if (businessUnit === null) {
      throw new Error('BusinessUnit is not loaded')
    }

    try {
      const paymentMethod = await api.paymentMethods.updatePaymentMethod(token, providerId, businessUnit.id, payload)

      const paymentMethods = mergeById(state.allowedPaymentMethods.elements, [paymentMethod])

      commit(UPDATE_ALLOWED_PAYMENT_METHODS, paymentMethods)
    } catch (error) {
      if (error.response) {
        const { status } = error.response
        if (status === 401) {
          dispatch('logout')
        }
      }
    }
  }
}

export default actions
